import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { buscarEscritorioPorId } from '@/features/escritorio';
import type { CustomFieldsState } from '../types';
import {
  createCustomField,
  deleteCustomField,
  fetchCustomField,
  fetchCustomFields,
  updateCustomField,
} from './actions';

const initialState: CustomFieldsState = {
  escritorioId: null,
  listRequestId: null,
  fields: [],
  currentField: null,
  loading: false,
  saving: false,
  pendingReadCount: 0,
  pendingMutationCount: 0,
  error: null,
  status: 'idle',
  mutationStatus: 'idle',
};

const customFieldsSlice = createSlice({
  name: 'customFields',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.status = 'idle';
      state.mutationStatus = 'idle';
    },
    clearCurrentField: (state) => {
      state.currentField = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(buscarEscritorioPorId.pending, (state, action) => {
        if (state.escritorioId === action.meta.arg) return;
        state.escritorioId = action.meta.arg;
        state.fields = [];
        state.currentField = null;
        state.listRequestId = null;
      })
      .addCase(fetchCustomFields.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchCustomFields.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;
        state.fields = action.payload;
      })
      .addCase(fetchCustomField.fulfilled, (state, action) => {
        state.currentField = action.payload;
      })
      .addCase(createCustomField.fulfilled, (state, action) => {
        state.fields.push(action.payload);
        state.currentField = action.payload;
      })
      .addCase(updateCustomField.fulfilled, (state, action) => {
        const index = state.fields.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) {
          state.fields[index] = action.payload;
        }
        if (state.currentField?.id === action.payload.id) {
          state.currentField = action.payload;
        }
      })
      .addCase(deleteCustomField.fulfilled, (state, action) => {
        state.fields = state.fields.filter((f) => f.id !== action.payload);
        if (state.currentField?.id === action.payload) {
          state.currentField = null;
        }
      })
      .addMatcher(isAnyOf(fetchCustomFields.pending, fetchCustomField.pending), (state) => {
        state.pendingReadCount += 1;
        state.loading = true;
        state.status = 'loading';
        state.error = null;
      })
      .addMatcher(isAnyOf(fetchCustomFields.fulfilled, fetchCustomField.fulfilled), (state) => {
        state.pendingReadCount = Math.max(0, state.pendingReadCount - 1);
        state.loading = state.pendingReadCount > 0;
        state.status = state.loading ? 'loading' : 'success';
      })
      .addMatcher(
        isAnyOf(fetchCustomFields.rejected, fetchCustomField.rejected),
        (state, action) => {
          state.pendingReadCount = Math.max(0, state.pendingReadCount - 1);
          state.loading = state.pendingReadCount > 0;
          state.status = state.loading ? 'loading' : 'error';
          state.error = (action.payload as string) ?? state.error;
        },
      )
      .addMatcher(
        isAnyOf(createCustomField.pending, updateCustomField.pending, deleteCustomField.pending),
        (state) => {
          state.pendingMutationCount += 1;
          state.saving = true;
          state.mutationStatus = 'loading';
          state.error = null;
        },
      )
      .addMatcher(
        isAnyOf(
          createCustomField.fulfilled,
          updateCustomField.fulfilled,
          deleteCustomField.fulfilled,
        ),
        (state) => {
          state.pendingMutationCount = Math.max(0, state.pendingMutationCount - 1);
          state.saving = state.pendingMutationCount > 0;
          state.mutationStatus = state.saving ? 'loading' : 'success';
        },
      )
      .addMatcher(
        isAnyOf(createCustomField.rejected, updateCustomField.rejected, deleteCustomField.rejected),
        (state, action) => {
          state.pendingMutationCount = Math.max(0, state.pendingMutationCount - 1);
          state.saving = state.pendingMutationCount > 0;
          state.mutationStatus = state.saving ? 'loading' : 'error';
          state.error = (action.payload as string) ?? state.error;
        },
      );
  },
});

export default customFieldsSlice.reducer;
